import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { Icon, Button, Label } from '@iq/react-components';
import JsonEditor from '../../../JsonEditor';
import config from '../../../../config';
import { EVENT_TYPE_USER, EVENT_TYPE_DTMP, EVENT_TYPE_MACH } from '../../../../constants';
import {
  getUserEventFiltersSchema,
  getUserEventFiltersUiSchema,
  machEventFiltersSchema,
  machEventFiltersUiSchema,
  dtmpEventFiltersSchema,
  dtmpEventFiltersUiSchema,
} from './eventFiltersSchema';
import {
  useDtmpAlgorithmTypes,
  useEnabledEventTypes,
  useFilterParameters,
  usePointGroups,
  useRelatedEventTypes,
  useSelectedEventType,
  /* useFalseFilter */
} from '../EventTimelineState';
import { requestLimitedUsers, getLimitedUsers } from '../../../../bundles/ad';
import { getTagsByCategory } from '../../../../bundles/tags';
import { requestUserEventTypes } from '../../../../bundles/events';
import { getUserEvents } from '../../../../services';
import {
  checkIsOnline,
  displayNotification,
  getNotification,
} from '../../../../bundles/notifications';
import {
  setPollingDateRange,
  getPollingDateRange,
  getInitialPollingDateRange,
} from '../../../../bundles/application';

export const TypeSpecificEventFilters = React.memo((properties) => {
  const { panelId, site, availableUserEventTypes, availableIotEventTypes } = properties;

  const dispatch = useDispatch();
  const [enabledEventTypes] = useEnabledEventTypes();
  const [relatedEventTypes, onChangeRelatedEventTypes] = useRelatedEventTypes();
  const [selectedEventType] = useSelectedEventType();
  // Commented out until we decide to include the filter. Skipped for this release
  // const [, onChangeFalse] = useFalseFilter();
  const [filterParameters, onChangeFilterParameters] = useFilterParameters();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(
    filterParameters && Object.keys(filterParameters).length > 0
      ? filterParameters
      : { mach: {}, user: {}, dtmp: {} }
  );

  // Toggle visibility of filter sections (only used in "relatedEvents-all" mode)
  const [showMachFilters, setShowMachFilters] = useState(true);
  const [showDtmpFilters, setShowDtmpFilters] = useState(true);
  const [showUserFilters, setShowUserFilters] = useState(true);

  // Read relatedEventTypes via ref so the seeding effect doesn't re-run every
  // time a toggle click updates the provider's relatedEventTypes BehaviorSubject
  // (which would immediately overwrite the click).
  const relatedEventTypesRef = useRef(relatedEventTypes);
  relatedEventTypesRef.current = relatedEventTypes;

  // Seed toggle visibility only when the panel opens or when persisted show
  // flags change (i.e. after Apply writes new flags into filterParameters).
  // Never re-run because of relatedEventTypes changes – those happen as a
  // direct result of the toggle clicks themselves.
  useEffect(() => {
    if (!open) {
      return;
    }

    if (!selectedEventType) {
      const hasPersistedShowFlags =
        typeof filterParameters?.mach?.showMachEvents === 'boolean' ||
        typeof filterParameters?.dtmp?.showDtmpEvents === 'boolean' ||
        typeof filterParameters?.user?.showUserEvents === 'boolean';

      if (hasPersistedShowFlags) {
        setShowMachFilters(Boolean(filterParameters?.mach?.showMachEvents));
        setShowDtmpFilters(Boolean(filterParameters?.dtmp?.showDtmpEvents));
        setShowUserFilters(Boolean(filterParameters?.user?.showUserEvents));
        return;
      }

      setShowMachFilters(relatedEventTypesRef.current.includes(EVENT_TYPE_MACH));
      setShowDtmpFilters(relatedEventTypesRef.current.includes(EVENT_TYPE_DTMP));
      setShowUserFilters(relatedEventTypesRef.current.includes(EVENT_TYPE_USER));
      return;
    }

    setShowMachFilters(true);
    setShowDtmpFilters(true);
    setShowUserFilters(true);
  }, [
    open,
    selectedEventType,
    filterParameters?.mach?.showMachEvents,
    filterParameters?.dtmp?.showDtmpEvents,
    filterParameters?.user?.showUserEvents,
  ]);

  useEffect(() => {
    if (filterParameters) {
      setFormData((data) => ({
        ...data,
        mach: structuredClone(filterParameters.mach) || structuredClone(data.mach) || {},
        user: structuredClone(filterParameters.user) || structuredClone(data.user) || {},
        dtmp: structuredClone(filterParameters.dtmp) || structuredClone(data.dtmp) || {},
      }));
    }
  }, [filterParameters]);

  const panelDateRange = useSelector((state) => getPollingDateRange(state, panelId));

  const ref = useRef(null);
  const users = useSelector(getLimitedUsers);
  const tags = useSelector((state) => getTagsByCategory(state, 'events'));
  const pointGroups = usePointGroups();
  const algorithmTypes = useDtmpAlgorithmTypes();

  const handleClickOutside = useCallback((event) => {
    if (
      ref.current &&
      !(
        ref.current.contains(event.target) ||
        // event toggles class (toggle svg icons' parents)
        event.target?.classList?.contains('show-events') ||
        event.target?.parentElement?.classList?.contains('show-events') ||
        // react-select menu option class matching
        [...event.target.classList.values()]
          .map((v) => v.match(/^css-\w{6}-option$/))
          .some((match) => !!match)
      )
    ) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('click', handleClickOutside, true);
    } else {
      document.removeEventListener('click', handleClickOutside, true);
    }
  }, [open, handleClickOutside]);

  useEffect(() => {
    dispatch(requestLimitedUsers(site.id));
    dispatch(requestUserEventTypes());
  }, [site, dispatch]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;

    // Count MACH filters
    if (
      (selectedEventType === EVENT_TYPE_MACH ||
        (!selectedEventType && enabledEventTypes.includes(EVENT_TYPE_MACH))) &&
      formData.mach
    ) {
      if (formData.mach.eventType?.length > 0) count++;
      if (formData.mach.remoteSource?.length > 0) count++;
    }

    // Count DTMP filters
    if (
      (selectedEventType === EVENT_TYPE_DTMP ||
        (!selectedEventType && enabledEventTypes.includes(EVENT_TYPE_DTMP))) &&
      formData.dtmp
    ) {
      if (formData.dtmp.algorithmType?.length > 0) count++;
      if (formData.dtmp.eventType?.length > 0) count++;
    }

    // Count User filters
    if (
      (selectedEventType === EVENT_TYPE_USER ||
        (!selectedEventType && enabledEventTypes.includes(EVENT_TYPE_USER))) &&
      formData.user
    ) {
      if (formData.user.type?.length > 0) count++;
      if (!config.onPremise && formData.user.assignee?.length > 0) count++;
      if (!config.onPremise && formData.user.creator?.length > 0) count++;
      if (formData.user.tags?.length > 0) count++;
      if (formData.user.status && formData.user.status !== 'all') count++;
      if (formData.user.activity && formData.user.activity !== 'all') count++;
    }

    return count;
  }, [selectedEventType, formData, enabledEventTypes]);

  const onUserChange = ({ formData: updatedFormData }) => {
    let newData = structuredClone(updatedFormData);
    const { quickFilter: newQuickFilter } = newData;

    setFormData((data) => {
      const { user: previousUserData } = data;

      if (
        // selected quickfilter which was previously not selected, update status
        newQuickFilter !== 'none' &&
        ((!previousUserData?.quickFilter && newQuickFilter) ||
          previousUserData?.quickFilter !== newQuickFilter)
      ) {
        newData = { quickFilter: newQuickFilter, status: newQuickFilter };
      } else if (
        // deselected quickfilter, reset status to "all" if user hasn't specifically changed status after quick filter selection
        newQuickFilter === 'none' &&
        newQuickFilter !== previousUserData?.quickFilter &&
        newData.status === previousUserData?.status
      ) {
        newData.status = 'all';
      }

      // deselected previously selected, non- "none" quick filter - should reset date range to panel default
      const shouldResetDateRange =
        newQuickFilter === 'none' &&
        previousUserData?.quickFilter &&
        previousUserData.quickFilter !== 'none';

      return { ...data, user: { ...newData }, shouldResetDateRange };
    });
  };

  const onMachChange = ({ formData: updatedFormData }) => {
    setFormData((data) => ({ ...data, mach: updatedFormData }));
  };

  const onDtmpChange = ({ formData: updatedFormData }) => {
    setFormData((data) => ({ ...data, dtmp: updatedFormData }));
  };

  const onFiltersApply = useCallback(async () => {
    // Merge formData with current filterParameters to ensure we don't lose filters from other tabs
    const filterData = {
      mach: {
        ...(formData.mach || filterParameters?.mach),
        showMachEvents: showMachFilters,
      },
      user: {
        ...(formData.user || filterParameters?.user),
        showUserEvents: showUserFilters,
      },
      dtmp: {
        ...(formData.dtmp || filterParameters?.dtmp),
        showDtmpEvents: showDtmpFilters,
      },
    };

    const quickFilter = filterData.user.quickFilter || 'none';
    filterData.user.quickFilter = quickFilter;

    if (quickFilter === 'none') {
      const range = formData.shouldResetDateRange
        ? getInitialPollingDateRange(panelId)
        : panelDateRange;
      dispatch(setPollingDateRange(range, panelId));
    } else {
      try {
        const userEvents = await getUserEvents({
          site: site.id,
          org: site.org,
          state: [quickFilter],
        });

        const events = userEvents?.data ?? [];
        const firstEvent = events[0];
        const lastEvent = events.at(-1);

        const defaultRange = getInitialPollingDateRange(panelId);
        panelDateRange.startDate = new Date(firstEvent?.from ?? defaultRange.startDate).getTime();
        panelDateRange.endDate = new Date(lastEvent?.from ?? defaultRange.endDate).getTime();
        panelDateRange.label = undefined;

        dispatch(setPollingDateRange(panelDateRange, panelId));
      } catch (error) {
        console.error('Unable to fetch user events:', error);
        dispatch(checkIsOnline());
        dispatch(displayNotification(getNotification('getUserEvents', 'error')()));
      }
    }

    const nextRelatedEventTypes = [EVENT_TYPE_MACH, EVENT_TYPE_DTMP, EVENT_TYPE_USER].filter(
      (eventType) => {
        switch (eventType) {
          case EVENT_TYPE_MACH: {
            return filterData.mach.showMachEvents;
          }
          case EVENT_TYPE_DTMP: {
            return filterData.dtmp.showDtmpEvents;
          }
          case EVENT_TYPE_USER: {
            return filterData.user.showUserEvents;
          }
          default: {
            return false;
          }
        }
      }
    );

    if (!selectedEventType) {
      onChangeRelatedEventTypes(nextRelatedEventTypes);
    }

    onChangeFilterParameters(filterData);
    setShowMachFilters(filterData.mach.showMachEvents);
    setShowDtmpFilters(filterData.dtmp.showDtmpEvents);
    setShowUserFilters(filterData.user.showUserEvents);

    setFormData((data) => ({ ...data, shouldResetDateRange: false }));
    setOpen(false);
  }, [
    formData,
    dispatch,
    panelId,
    site,
    panelDateRange,
    onChangeFilterParameters,
    filterParameters?.mach,
    filterParameters?.user,
    filterParameters?.dtmp,
    showMachFilters,
    showUserFilters,
    showDtmpFilters,
    selectedEventType,
    onChangeRelatedEventTypes,
  ]);

  const onFiltersReset = () => {
    const hasSelectedEventType = Boolean(selectedEventType);
    const resetRelatedEventTypes = hasSelectedEventType ? [selectedEventType] : enabledEventTypes;

    const resetData = {
      mach: {
        showMachEvents: resetRelatedEventTypes.includes(EVENT_TYPE_MACH),
      },
      user: {
        quickFilter: 'none',
        showUserEvents: resetRelatedEventTypes.includes(EVENT_TYPE_USER),
      },
      dtmp: {
        showDtmpEvents: resetRelatedEventTypes.includes(EVENT_TYPE_DTMP),
      },
    };
    onChangeFilterParameters(resetData);

    setShowMachFilters(resetData.mach.showMachEvents);
    setShowDtmpFilters(resetData.dtmp.showDtmpEvents);
    setShowUserFilters(resetData.user.showUserEvents);

    if (!selectedEventType) {
      onChangeRelatedEventTypes(resetRelatedEventTypes);
    }

    setOpen(false);
  };

  const userEventsFilterSchema = useMemo(
    () => getUserEventFiltersSchema(availableUserEventTypes, tags, users),
    [availableUserEventTypes, tags, users]
  );
  const userEventsFilterUiSchema = useMemo(() => getUserEventFiltersUiSchema(formData), [formData]);
  const machEventSchema = useMemo(
    () => machEventFiltersSchema(availableIotEventTypes?.mach || [], pointGroups || []),
    [availableIotEventTypes, pointGroups]
  );
  const dtmpEventSchema = useMemo(
    () => dtmpEventFiltersSchema(availableIotEventTypes?.dtmp?.eventTypes || [], algorithmTypes),
    [availableIotEventTypes, algorithmTypes]
  );

  // Toggle handlers only update local show-state. `onChangeRelatedEventTypes` is
  // called exclusively from Apply/Reset so the fetch only changes when the user
  // explicitly commits their choices, not on every toggle click.
  const onMachToggle = useCallback(() => {
    setShowMachFilters((value) => {
      if (value && !showDtmpFilters && !showUserFilters) {
        return value; // keep at least one family enabled
      }
      return !value;
    });
  }, [showDtmpFilters, showUserFilters]);

  const onDtmpToggle = useCallback(() => {
    setShowDtmpFilters((value) => {
      if (value && !showMachFilters && !showUserFilters) {
        return value;
      }
      return !value;
    });
  }, [showMachFilters, showUserFilters]);

  const onUserToggle = useCallback(() => {
    setShowUserFilters((value) => {
      if (value && !showMachFilters && !showDtmpFilters) {
        return value;
      }
      return !value;
    });
  }, [showMachFilters, showDtmpFilters]);

  const classes = classnames('event-filters-component', { open });

  const shouldRenderFilterContainer = useCallback(
    (eventType) => {
      return (
        enabledEventTypes.includes(eventType) &&
        (!selectedEventType || selectedEventType === eventType)
      );
    },
    [selectedEventType, enabledEventTypes]
  );

  const shouldRenderFilters = useCallback(
    (eventType) => {
      switch (eventType) {
        case EVENT_TYPE_MACH: {
          return showMachFilters;
        }
        case EVENT_TYPE_DTMP: {
          return showDtmpFilters;
        }
        case EVENT_TYPE_USER: {
          return showUserFilters;
        }
        default: {
          return false;
        }
      }
    },
    [showMachFilters, showDtmpFilters, showUserFilters]
  );

  return (
    <div
      ref={ref}
      className={classes}
    >
      <button
        type="button"
        className="header"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <div className="label">
          Filters
          {activeFilterCount > 0 && <span className="filter-count-badge">{activeFilterCount}</span>}
        </div>
        <div className="icon-container">
          <Icon
            icon="he-down"
            size="xs"
          />
        </div>
      </button>
      <div className="content">
        <div className="actions">
          <Label>Filters</Label>
          <div className="actions-button">
            <Button
              onClick={onFiltersReset}
              activity="secondary"
              slim
            >
              Reset
            </Button>
            <Button
              slim
              onClick={onFiltersApply}
            >
              Apply
            </Button>
          </div>
        </div>
        <div className="headers">
          {shouldRenderFilterContainer(EVENT_TYPE_MACH) && (
            <div className="mach">
              <div className="title">Mach IoT Events</div>
              {!selectedEventType && enabledEventTypes.length > 1 && (
                <Button
                  design="text"
                  onClick={onMachToggle}
                  tooltip={showMachFilters ? 'Hide' : 'Show'}
                >
                  <Icon
                    className={`show-events ${showMachFilters ? 'on' : ''}`}
                    icon={showMachFilters ? 'toggle-on' : 'toggle-off'}
                    size="l"
                  />
                </Button>
              )}
            </div>
          )}
          {shouldRenderFilterContainer(EVENT_TYPE_DTMP) && (
            <div className="dtmp">
              <div className="title">DTMP IoT Events</div>
              {!selectedEventType && enabledEventTypes.length > 1 && (
                <Button
                  design="text"
                  onClick={onDtmpToggle}
                  tooltip={showDtmpFilters ? 'Hide' : 'Show'}
                >
                  <Icon
                    size="l"
                    className={`show-events ${showDtmpFilters ? 'on' : ''}`}
                    icon={showDtmpFilters ? 'toggle-on' : 'toggle-off'}
                  />
                </Button>
              )}
            </div>
          )}
          {shouldRenderFilterContainer(EVENT_TYPE_USER) && (
            <div className="user">
              <div className="title">User Events</div>
              {!selectedEventType && enabledEventTypes.length > 1 && (
                <Button
                  design="text"
                  onClick={onUserToggle}
                  tooltip={showUserFilters ? 'Hide' : 'Show'}
                >
                  <Icon
                    size="l"
                    className={`show-events ${showUserFilters ? 'on' : ''}`}
                    icon={showUserFilters ? 'toggle-on' : 'toggle-off'}
                  />
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="body">
          {shouldRenderFilterContainer(EVENT_TYPE_MACH) && (
            <div className="mach">
              {shouldRenderFilters(EVENT_TYPE_MACH) && (
                <>
                  <JsonEditor
                    formData={formData.mach}
                    onFormChange={onMachChange}
                    schema={machEventSchema}
                    uiSchema={machEventFiltersUiSchema}
                    initialEditMode={true}
                    showEditButton={false}
                    showButtons={false}
                  />
                  {/* Commented out until we decide to include the filter. Skipped for this release */}
                  {/* <div className="false">
                      <div className="false-alarm-filter-title">
                        <label>False alarm filtering</label>
                        <Button
                          design="text"
                          slim
                          icon={
                            <Icon
                              icon="he-info"
                              size="s"
                            />
                          }
                          tooltip={
                            'Turning the False alarm filtering on will hide events labeled as "false" from the list and timeline view.'
                          }
                          onClick={(event) => event.preventDefault()}
                        ></Button>
                      </div>
                      <div className="false-alarm-filter">
                        <Label>Hide likely false events</Label>
                        {selectedEventType === 'all' && (
                          <Button
                            design="text"
                            tooltip={hideFalseEvents ? 'Hide' : 'Show'}
                          >
                            <Icon
                              size="m"
                              className={`show-false-events ${hideFalseEvents ? 'on' : ''}`}
                              icon={hideFalseEvents ? 'toggle-on' : 'toggle-off'}
                              onClick={() => onFalseToggle(!hideFalseEvents)}
                            ></Icon>
                          </Button>
                        )}
                      </div>
                    </div> */}
                </>
              )}
            </div>
          )}
          {shouldRenderFilterContainer(EVENT_TYPE_DTMP) && (
            <div className="dtmp">
              {shouldRenderFilters(EVENT_TYPE_DTMP) && (
                <JsonEditor
                  formData={formData.dtmp || {}}
                  onFormChange={onDtmpChange}
                  schema={dtmpEventSchema}
                  uiSchema={dtmpEventFiltersUiSchema}
                  initialEditMode={true}
                  showEditButton={false}
                  showButtons={false}
                />
              )}
            </div>
          )}
          {shouldRenderFilterContainer(EVENT_TYPE_USER) && (
            <div className="user">
              {shouldRenderFilters(EVENT_TYPE_USER) && (
                <JsonEditor
                  formData={formData.user}
                  onFormChange={onUserChange}
                  schema={userEventsFilterSchema}
                  uiSchema={userEventsFilterUiSchema}
                  initialEditMode={true}
                  showEditButton={false}
                  showButtons={false}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default TypeSpecificEventFilters;
